import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = { primary: '#2b3380', success: '#4caf50', danger: '#f44336' };

function WhatsAppCannedResponses({ user, onLogout }) {
  const [responses, setResponses] = useState([]);
  const [stats, setStats] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [editingResponse, setEditingResponse] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    shortcut: '',
    message: '',
    category: 'support',
    is_team_wide: true
  });

  const categories = ['greeting', 'support', 'sales', 'closing', 'other'];

  useEffect(() => {
    fetchResponses();
    fetchStats();
  }, []);

  const fetchResponses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/canned-responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponses(response.data.responses || []);
    } catch (error) {
      console.error('Error fetching canned responses:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/whatsapp/canned-responses/stats/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data || {});
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingResponse) {
        await axios.put(
          `${API}/api/whatsapp/canned-responses/${editingResponse.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Canned response updated successfully!');
      } else {
        await axios.post(
          `${API}/api/whatsapp/canned-responses`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Canned response created successfully!');
      }
      
      setShowCreate(false);
      setEditingResponse(null);
      setFormData({
        title: '',
        shortcut: '',
        message: '',
        category: 'support',
        is_team_wide: true
      });
      fetchResponses();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error saving canned response');
    }
  };

  const handleEdit = (response) => {
    setEditingResponse(response);
    setFormData({
      title: response.title,
      shortcut: response.shortcut,
      message: response.message,
      category: response.category,
      is_team_wide: response.is_team_wide
    });
    setShowCreate(true);
  };

  const handleDelete = async (responseId) => {
    if (!window.confirm('Are you sure you want to delete this canned response?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/whatsapp/canned-responses/${responseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Canned response deleted successfully!');
      fetchResponses();
      fetchStats();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error deleting canned response');
    }
  };

  const filteredResponses = responses.filter(resp => {
    const matchesCategory = selectedCategory === 'all' || resp.category === selectedCategory;
    const matchesSearch = !searchTerm || 
      resp.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resp.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resp.shortcut.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <Layout user={user} onLogout={onLogout}>
      <div style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ color: COLORS.primary, margin: 0 }}>💬 Canned Responses</h1>
          <button
            onClick={() => setShowCreate(!showCreate)}
            style={{
              padding: '12px 24px',
              backgroundColor: COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {showCreate ? '✖ Cancel' : '➕ Create Response'}
          </button>
        </div>

        {/* Stats Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Total Responses</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.primary }}>{stats.total || 0}</div>
          </div>
          {stats.by_category && Object.entries(stats.by_category).map(([cat, count]) => (
            <div key={cat} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px', textTransform: 'capitalize' }}>{cat}</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: COLORS.success }}>{count}</div>
            </div>
          ))}
        </div>

        {/* Create/Edit Form */}
        {showCreate && (
          <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '24px' }}>
            <h3 style={{ color: COLORS.primary, marginTop: 0 }}>{editingResponse ? 'Edit' : 'Create'} Canned Response</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., Greeting - Morning"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Shortcut * <span style={{ color: '#666', fontSize: '12px' }}>(Type this in chat to insert)</span></label>
                <input
                  type="text"
                  value={formData.shortcut}
                  onChange={(e) => setFormData({ ...formData, shortcut: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  placeholder="e.g., /hello or /thanks"
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Message * <span style={{ color: '#666', fontSize: '12px' }}>(Use {'{'}variable{'}'} for dynamic content)</span></label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'inherit'
                  }}
                  placeholder="e.g., Hello {'{'}customer_name{'}'}! How can I help you today?"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Visibility</label>
                  <select
                    value={formData.is_team_wide}
                    onChange={(e) => setFormData({ ...formData, is_team_wide: e.target.value === 'true' })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value="true">Team-wide</option>
                    <option value="false">Personal</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                style={{
                  padding: '12px 24px',
                  backgroundColor: COLORS.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginRight: '8px'
                }}
              >
                {editingResponse ? 'Update' : 'Create'} Response
              </button>
              {editingResponse && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingResponse(null);
                    setShowCreate(false);
                    setFormData({
                      title: '',
                      shortcut: '',
                      message: '',
                      category: 'support',
                      is_team_wide: true
                    });
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#ddd',
                    color: '#333',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
              )}
            </form>
          </div>
        )}

        {/* Filters */}
        <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginBottom: '16px', display: 'flex', gap: '16px' }}>
          <div style={{ flex: 1 }}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="🔍 Search responses..."
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>
          <div style={{ width: '200px' }}>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Responses List */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          {filteredResponses.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
              <div style={{ fontSize: '18px', fontWeight: '500', marginBottom: '8px' }}>No Canned Responses Yet</div>
              <div style={{ fontSize: '14px' }}>Create your first canned response to speed up customer communication</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Title</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Shortcut</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Message</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Category</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Usage</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Visibility</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResponses.map((response) => (
                    <tr key={response.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500' }}>{response.title}</div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <code style={{ backgroundColor: '#f5f5f5', padding: '4px 8px', borderRadius: '4px', fontSize: '12px' }}>
                          {response.shortcut}
                        </code>
                      </td>
                      <td style={{ padding: '12px', maxWidth: '300px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px', color: '#666' }}>
                          {response.message}
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          textTransform: 'capitalize'
                        }}>
                          {response.category}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <span style={{ fontWeight: '500', color: COLORS.success }}>{response.usage_count || 0}</span>
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {response.is_team_wide ? '👥 Team' : '👤 Personal'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleEdit(response)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#f5f5f5',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            marginRight: '4px'
                          }}
                        >
                          ✏️ Edit
                        </button>
                        <button
                          onClick={() => handleDelete(response.id)}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#ffebee',
                            color: COLORS.danger,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Top Used Responses */}
        {stats.top_used && stats.top_used.length > 0 && (
          <div style={{ marginTop: '24px', backgroundColor: 'white', padding: '24px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: COLORS.primary, marginTop: 0 }}>🔥 Most Used Responses</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              {stats.top_used.map((resp, idx) => (
                <div key={resp.shortcut} style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>#{idx + 1} {resp.title}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>{resp.shortcut}</div>
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: COLORS.success }}>{resp.usage_count}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default WhatsAppCannedResponses;